<template>
  <div>
    <OurDivider v-if="upperlined" size="xl" no-top-padding />

    <OurEmptyBlock
      v-if="!hideEmptyStateForNesting && !list?.length"
      :description="emptyDescription"
      :title="emptyTitle"
    />

    <draggable
      v-else
      v-bind="dragOptions"
      :list="list"
      :group="{ name: group }"
      class="list"
      handle=".drag-icon"
      drag-class="draggable-item"
      :class="themeClass"
      :data-cy="dataCy"
      :move="onDragMove"
      @end="onDragEnd"
    >
      <div
        v-for="(row, index) in list"
        :id="row.id"
        :key="row.id"
        class="list-items"
        :data-cy="`${dataCy}-item`"
      >
        <div class="list-item-parent" :data-cy="`${dataCy}-item-${index}`">
          <div v-if="isDraggable" class="list-item-left">
            <OurSvgIcon
              class="icon-dial-numbers drag-icon"
              :src="icons.DialNumbers"
              color="gray"
              variant="light"
            />
          </div>

          <div
            class="list-item-title-wrap"
            :class="{ 'list-item-title-wrap--draggable': isDraggable }"
          >
            <template v-if="collapsibleNesting && rowHasNestedChildren(row)">
              <slot
                name="branch-toggle"
                :item="row"
                :collapsed="Boolean(effectiveCollapseMap[row.id])"
                :toggle-collapse="() => toggleBranchCollapse(row.id)"
              >
                <OurSvgIcon
                  class="categories-tree-table-branch-toggle table-list-branch-toggle shrink-0 cursor-pointer transition-transform duration-150"
                  :class="{ 'table-list-branch-toggle--collapsed': effectiveCollapseMap[row.id] }"
                  :src="icons.ArrowDown"
                  color="gray"
                  variant="light"
                  :data-cy="`${dataCy}-branch-toggle`"
                  @click.stop="toggleBranchCollapse(row.id)"
                />
              </slot>
            </template>

            <div class="list-item-title-wrap__body">
              <slot :item="row">
                <div
                  class="list-item-left-title cursor-pointer"
                  :class="{ left: !isDraggable }"
                  @click="openChapter(row.id)"
                >
                  {{ row.name }}
                </div>
              </slot>
            </div>
          </div>

          <div v-if="!row.isHiddenAction" class="list-item-right">
            <div v-if="isShownIconsSlot" class="icon-wrapper">
              <slot name="icons" :item="row" />
            </div>

            <OurSvgIcon
              v-if="havePermissionDelete"
              interactive
              class="icon-trash icon"
              :src="icons.Trash"
              color="red"
              :data-cy="`${dataCy}-delete`"
              :tooltip="$t('button.delete')"
              @click="onClickDelete(row.id, row.name)"
            />

            <OurSvgIcon
              v-if="havePermissionEdit"
              interactive
              class="icon-edit"
              :src="icons.Edit"
              color="gray"
              :data-cy="`${dataCy}-edit`"
              :tooltip="$t('button.edit')"
              @click="onClickEdit(row.id)"
            />
          </div>
        </div>

        <OurTableList
          v-if="shouldRenderNestedTable(row)"
          :nesting="nesting"
          hide-empty-state-for-nesting
          class="list-nested"
          :list="nestedRowChildren(row)"
          :group="group"
          :data-cy="`${dataCy}-table`"
          :collapsible-nesting="collapsibleNesting"
          :branch-collapse-map="collapsibleNesting ? effectiveCollapseMap : undefined"
          :have-permission-delete="havePermissionDelete"
          :have-permission-edit="havePermissionEdit"
          :is-draggable="isDraggable"
          @clickDelete="onClickDelete"
          @clickEdit="onClickEdit"
          @dragSort="onDragEnd"
        >
          <template #default="{ item }">
            <slot :item="item">
              <div class="list-item-left-title">{{ item.name }}</div>
            </slot>
          </template>

          <template #icons="{ item }">
            <slot name="icons" :item="item" />
          </template>
        </OurTableList>
      </div>
    </draggable>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { getThemeClass } from "@/services/_bit/UiService";

import OurSvgIcon from "@/components/_bit/IconSvg";
import OurDivider from "@/components/_bit/Divider";

export default {
  name: "OurTableList",

  components: {
    OurSvgIcon,
    OurDivider,
    draggable,
  },

  props: {
    /**
     * Set data list for component.
     */
    list: {
      type: Array,
      default: () => [],
    },

    /**
     * Set group name.
     */
    group: {
      type: String,
      default: "",
    },

    /**
     * Enable or disable nesting.
     */
    nesting: {
      type: Boolean,
      default: false,
    },

    /**
     * Sets empty state title when no data in a table.
     */
    emptyTitle: {
      type: String,
      default: "",
    },

    /**
     * Sets empty state description when no data in a table.
     */
    emptyDescription: {
      type: String,
      default: "",
    },

    /**
     * Add line divider above the list.
     */
    upperlined: {
      type: Boolean,
      default: false,
    },

    /**
     * Sets data-cy attribute for correct element sampling in tests.
     */
    dataCy: {
      type: String,
      default: "",
    },

    /**
     * @ignore
     * Disable empty state for nested elements if empty (internal prorps).
     */
    hideEmptyStateForNesting: {
      type: Boolean,
      default: false,
    },

    havePermissionDelete: {
      type: Boolean,
      default: false,
    },

    havePermissionEdit: {
      type: Boolean,
      default: false,
    },

    isDraggable: {
      type: Boolean,
      default: true,
    },

    /**
     * When true with nesting, nested lists mount only if the row id is not collapsed (internal map or branchCollapseMap prop).
     */
    collapsibleNesting: {
      type: Boolean,
      default: false,
    },

    /**
     * Optional controlled collapse map (rowId -> true when collapsed). Omit for internal state on root instance.
     */
    branchCollapseMap: {
      type: Object,
      default: undefined,
    },
  },

  data() {
    return {
      localBranchCollapseMap: {},
    };
  },

  computed: {
    dragOptions() {
      return {
        animation: 200,
        group: this.group,
        disabled: false,
        ghostClass: "ghost",
      };
    },

    icons() {
      return {
        Trash: require("@/components/_bit/IconSvg/icons/Trash.svg"),
        DialNumbers: require("@/components/_bit/IconSvg/icons/Dial-numbers.svg"),
        Edit: require("@/components/_bit/IconSvg/icons/Edit.svg"),
        ArrowDown: require("@/components/_bit/IconSvg/icons/Angle-down.svg"),
      };
    },

    /**
     * Shared object for collapsed branch ids across recursive instances (internal or controlled prop).
     */
    effectiveCollapseMap() {
      if (!this.collapsibleNesting) {
        return null;
      }

      return this.branchCollapseMap != null ? this.branchCollapseMap : this.localBranchCollapseMap;
    },

    themeClass() {
      return getThemeClass();
    },

    isShownIconsSlot() {
      return !!this.$scopedSlots["icons"];
    },
  },

  methods: {
    rowHasNestedChildren(row) {
      return Array.isArray(row?.children) && row.children.length > 0;
    },

    /**
     * Nested draggable must mount even when children is empty — otherwise there is no drop target to nest under this row.
     */
    nestedRowChildren(row) {
      if (!Array.isArray(row.children)) {
        this.$set(row, "children", []);
      }

      return row.children;
    },

    shouldRenderNestedTable(row) {
      if (!this.nesting || row.isDisabledNesting) {
        return false;
      }

      if (!this.collapsibleNesting || !this.effectiveCollapseMap) {
        return true;
      }

      return !this.effectiveCollapseMap[row.id];
    },

    collectBranchIdsWithChildren(nodes) {
      const ids = [];

      if (!Array.isArray(nodes)) {
        return ids;
      }

      nodes.forEach((node) => {
        const children = node.children;

        if (Array.isArray(children) && children.length > 0) {
          ids.push(node.id);
          ids.push(...this.collectBranchIdsWithChildren(children));
        }
      });

      return ids;
    },

    /**
     * Toggle collapsed state for a branch row (collapsible nesting only).
     */
    toggleBranchCollapse(branchId) {
      const map = this.effectiveCollapseMap;

      if (!this.collapsibleNesting || !map) {
        return;
      }

      if (map[branchId]) {
        this.$delete(map, branchId);
      } else {
        this.$set(map, branchId, true);
      }
    },

    expandAllBranches() {
      const map = this.effectiveCollapseMap;

      if (!this.collapsibleNesting || !map) {
        return;
      }

      Object.keys(map).forEach((id) => {
        this.$delete(map, id);
      });
    },

    collapseAllBranches() {
      const map = this.effectiveCollapseMap;

      if (!this.collapsibleNesting || !map) {
        return;
      }

      this.collectBranchIdsWithChildren(this.list).forEach((id) => {
        this.$set(map, id, true);
      });
    },

    onDragMove(event) {
      const isDisabledNestingItem = event.draggedContext.element.isDisabledNesting;
      const isNestingAction = !event.relatedContext.element?.isDisabledNesting;

      if (isDisabledNestingItem && isNestingAction) {
        return false;
      }
    },

    emitDragSort() {
      if (!Array.isArray(this.list)) {
        this.$emit("dragSort", []);

        return;
      }

      this.$emit("dragSort", this.prepareSortData(this.list));
    },

    onDragEnd() {
      this.emitDragSort();
    },

    onClickEdit(id) {
      this.$emit("clickEdit", id);
    },

    onClickDelete(id, title) {
      this.$emit("clickDelete", id, title);
    },

    openChapter(id) {
      this.$emit("clickChapter", id);
    },

    prepareSortData(list, parentId) {
      let sortData = [];

      list.forEach((item) => {
        let hasItemChildren = item?.children?.length;

        if (hasItemChildren) {
          let childrenItem = this.prepareSortData(item.children, item.id);

          childrenItem.forEach((item) => {
            sortData.push(item);
          });
        }

        let parentItem = { id: item.id, parentId: 0 || parentId };

        sortData.push(parentItem);
      });

      return sortData;
    },
  },
};
</script>

<style lang="postcss" scoped>
.ghost {
  @apply bg-blue-100 bg-opacity-50;
}

.draggable-item {
  @apply rounded-lg bg-gray-200;
  @apply border-b-0;
}

.sortable-chosen {
  @apply rounded-lg;

  .list-item-parent {
    @apply border-b-0;
  }
}

.list {
  &-nested {
    @apply ml-6;
  }

  &-item {
    &-parent {
      @apply flex items-center justify-between;
      @apply py-4;
      @apply border border-l-0 border-r-0 border-t-0 border-solid border-gray-100;

      &:hover {
        &:deep() {
          .icon {
            @apply opacity-100;
            @apply transition duration-100 ease-in-out;

            &-edit {
              & g [fill] {
                @apply text-gray-500;
              }
            }
          }
        }

        .icon-wrapper {
          @apply opacity-100;
          @apply transition duration-100 ease-in-out;
        }
      }

      &:has(.categories-tree-table-branch-toggle):not(:has(.table-list-branch-toggle--collapsed)) {
        @apply border-b-0;
      }
    }

    &-left {
      @apply flex items-center;

      &-title {
        @apply flex-auto;
        @apply text-base font-normal text-gray-900;
        @apply ml-5 pt-px;
        word-break: break-word;
        &.left {
          margin-left: 0;
        }
      }
    }

    &-title-wrap {
      @apply flex min-w-0 flex-1 items-center gap-2;

      &__body {
        @apply min-w-0 flex-1;
      }

      &--draggable {
        @apply ml-5 pt-px;

        &:deep(.list-item-left-title) {
          margin-left: 0;
        }
      }

      &:deep(.list-item-left-title.left) {
        margin-left: 0;
      }
    }

    &-right {
      @apply flex flex-none items-center justify-end;
      @apply w-28 space-x-5;
    }
  }

  &-items {
    &:last-child {
      .list-item-parent {
        @apply border-b-0;
      }
    }
  }

  &-nested {
    .list-items {
      .list-item-parent {
        @apply border-b;
      }

      .list-item-parent:has(.categories-tree-table-branch-toggle):not(
          :has(.table-list-branch-toggle--collapsed)
        ) {
        @apply border-b-0;
      }

      &:last-child {
        .list-item-parent {
          @apply border-b-0;
        }
      }
    }
  }
}

.table-list-branch-toggle {
  @apply opacity-100;

  &--collapsed {
    transform: rotate(-90deg);
  }
}

.icon {
  @apply opacity-0;

  &-wrapper {
    @apply flex items-center space-x-5;
    @apply opacity-0;
  }

  &-edit {
    &:deep() {
      & g [fill] {
        @apply text-gray-300;
      }
    }
  }

  &-dial-numbers {
    @apply cursor-move;

    &:deep() {
      & g [fill] {
        @apply text-gray-400;
        @apply opacity-100;
      }

      &:hover {
        g [fill] {
          @apply text-gray-500;
        }
      }
    }
  }
}
</style>
